import { useState, useEffect } from 'react';
import { Modal, Button, Label, TextInput, Select, Alert } from 'flowbite-react';
import { descriptorsApi, type Descriptor } from '../../api/descriptors';
import type { OrgEmailAddressResponse } from '../../types/api';

interface EditOrgEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (emailData: {
    emailAddress: string;
    emailTypeDescriptorId?: number | null;
    primaryEmailIndicator: boolean;
  }) => Promise<void>;
  email?: OrgEmailAddressResponse | null;
  title?: string;
}

export const EditOrgEmailModal: React.FC<EditOrgEmailModalProps> = ({
  isOpen,
  onClose,
  onSave,
  email,
  title
}) => {
  const [formData, setFormData] = useState({
    emailAddress: '',
    emailTypeDescriptorId: undefined as number | undefined,
    primaryEmailIndicator: false
  });
  const [emailTypes, setEmailTypes] = useState<Descriptor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);

  // Load email type descriptors
  useEffect(() => {
    const loadEmailTypes = async () => {
      setIsLoadingTypes(true);
      try {
        const types = await descriptorsApi.getByTypeName('Organization Email Type');
        setEmailTypes(types);
      } catch (err) {
        console.error('Failed to load email types:', err);
      } finally {
        setIsLoadingTypes(false);
      }
    };

    if (isOpen) {
      loadEmailTypes();
    }
  }, [isOpen]);

  // Reset form when modal opens/closes or email changes
  useEffect(() => {
    if (email) {
      setFormData({
        emailAddress: email.emailAddress || '',
        emailTypeDescriptorId: email.emailTypeDescriptorId ?? undefined,
        primaryEmailIndicator: email.primaryEmailIndicator || false
      });
    } else {
      setFormData({
        emailAddress: '',
        emailTypeDescriptorId: undefined,
        primaryEmailIndicator: false
      });
    }
    setError(null);
  }, [email, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.emailAddress)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSaving(true);

    try {
      await onSave({
        emailAddress: formData.emailAddress,
        emailTypeDescriptorId: formData.emailTypeDescriptorId || null,
        primaryEmailIndicator: formData.primaryEmailIndicator
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save email');
    } finally {
      setIsSaving(false);
    }
  };

  const modalTitle = title || (email ? 'Edit Organization Email' : 'Add Organization Email');

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="lg" popup>
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{modalTitle}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert color="failure" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div>
            <Label htmlFor="emailAddress">Email Address *</Label>
            <TextInput
              id="emailAddress"
              type="email"
              value={formData.emailAddress}
              onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
              required
              placeholder="example@district.org"
              maxLength={254}
            />
          </div>

          <div>
            <Label htmlFor="emailType">Email Type</Label>
            <Select
              id="emailType"
              value={formData.emailTypeDescriptorId || ''}
              onChange={(e) => setFormData({
                ...formData,
                emailTypeDescriptorId: e.target.value ? Number(e.target.value) : undefined
              })}
              disabled={isLoadingTypes}
            >
              <option value="">Select type...</option>
              {emailTypes.map((type) => (
                <option key={type.descriptorId} value={type.descriptorId}>
                  {type.shortDescription}
                </option>
              ))}
            </Select>
            <p className="mt-1 text-xs text-gray-500">
              Categorize this email (e.g., Human Resources, Transportation)
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="primaryEmailIndicator"
              checked={formData.primaryEmailIndicator}
              onChange={(e) => setFormData({ ...formData, primaryEmailIndicator: e.target.checked })}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <Label htmlFor="primaryEmailIndicator">Set as primary organization email</Label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button color="light" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || isLoadingTypes}>
              {isSaving ? 'Saving...' : email ? 'Save Changes' : 'Add Email'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
